import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';

import { useQueryClient } from 'react-query';

import PATHS, { AUTH_PREFIX } from 'constants/paths';
import useMeQuery from 'hooks/useMeQuery/useMeQuery';
import useSubscription from 'services/common/useSubscription/useSubscription';
import useExercisesSetsStore from 'storages/exercisesSets/exercisesSets';
import useExercisesStore from 'storages/exercises/exercises';
import useRedirectsStore from 'storages/redirects/useRedirects';
import useSheetsStore from 'storages/sheets/sheets';
import { AppEvent } from 'types/AppEvent';
import authManager from 'utils/authManager/authManager';
// eslint-disable-next-line camelcase
import useExercisesToDisplay_sets from '../storages/exercisesToDisplay/useExercisesToDisplay_sets';

const AuthContext = createContext(null);

const { Provider } = AuthContext;

const localData = authManager.getCookieAuthData();
const initialAuthData = {
  token: null,
};

export const authServices = {
  onLogin: token => {
    authManager.setCookieAuthData({ token });
  },
  onLogout: () => {
    authManager.removeCookieAuthData();
    sessionStorage.clear();
  },
};

function AuthProvider(props) {
  const { setRedirectOnLogin, setRedirectOnLogout, pruneRedirectOnLogin } = useRedirectsStore();
  const [authData, setAuthData] = useState(localData || initialAuthData);
  const [token, setToken] = useState(localData?.token || initialAuthData.token);

  const sheetsStore = useSheetsStore();
  const exercisesStore = useExercisesStore();
  const exercisesSetsStore = useExercisesSetsStore();
  const exercisesToDisplaySets = useExercisesToDisplay_sets();
  const queryClient = useQueryClient();

  const updateAuthStorage = useCallback(newData => {
    setAuthData(prev => {
      const concatData = { ...prev, ...newData };
      authManager.setCookieAuthData(concatData);
      return concatData;
    });
  }, []);

  useEffect(() => {
    if (!authData?.token) return;
    setRedirectOnLogout(authData.isSchoolAccount ? PATHS.LOGIN_BY_SCHOOL : PATHS.LOGIN);
  }, [authData?.isSchoolAccount, authData?.token, setRedirectOnLogout]);

  const onLogout = useCallback(async () => {
    authServices.onLogout();
    setToken(initialAuthData.token);
    setAuthData({ token: false });

    queryClient.clear();

    sheetsStore.reset();
    exercisesStore.reset();
    exercisesSetsStore.reset();
    exercisesToDisplaySets.reset();

    pruneRedirectOnLogin();
  }, []);

  const onLogin = useCallback((newToken, additionalData) => {
    authServices.onLogin(newToken);
    if (additionalData) setAuthData(additionalData);
    setToken(newToken);
  }, []);

  // Single /me fetch when token is present; shared cache for the rest of the app.
  useMeQuery({
    enabled: !!token,
    onSuccess: data => {
      if (data) {
        updateAuthStorage({ token, ...data });
      } else {
        onLogout();
      }
    },
  });

  useEffect(() => {
    if (!token && !window.location.pathname.includes(AUTH_PREFIX)) setRedirectOnLogin(window.location.pathname);
  }, [token, setRedirectOnLogin]);

  useEffect(() => {
    const cookiesAuthData = authManager.getCookieAuthData();
    if (cookiesAuthData) {
      setAuthData(cookiesAuthData);
    } else setAuthData({ token: false });
  }, []);

  useSubscription(AppEvent.OUTDATED_TOKEN, onLogout);

  const authDataValue = useMemo(
    () => ({
      ...authData,
      updateAuthStorage,
      onLogin,
      onLogout,
    }),
    [authData, updateAuthStorage, onLogin, onLogout],
  );

  return <Provider value={authDataValue} {...props} />;
}

export const useAuthContext = () => useContext(AuthContext);

export default AuthProvider;
